import { useEffect, useState, useMemo } from 'react';
import { userApi } from '../../../api/adminApi';
import type { AdminUser, AdminCharacter } from '../../../types/adminTypes';

// ── Class metadata ────────────────────────────────────────────────────────────

const CLASS_META: Record<string, { label: string; icon: string; color: string }> = {
  warrior: { label: 'Guerreiro',  icon: '⚔',  color: '#c87c30' },
  mage:    { label: 'Mago',       icon: '🔮', color: '#8060d0' },
  rogue:   { label: 'Ladino',     icon: '🗡',  color: '#40a060' },
  archer:  { label: 'Arqueiro',   icon: '🏹', color: '#30a0c0' },
  cleric:  { label: 'Clérigo',    icon: '✨', color: '#e0c040' },
};

// ── Character detail panel ────────────────────────────────────────────────────

function CharacterPanel({ char }: { char: AdminCharacter }) {
  const meta  = CLASS_META[char.class] ?? { label: char.class, icon: '⚔', color: '#888' };
  const stats = char.stats;

  return (
    <div style={{
      margin:       '10px 0 4px 32px',
      padding:      '14px 18px',
      background:   'rgba(255,255,255,0.03)',
      border:       `1px solid ${meta.color}44`,
      borderLeft:   `3px solid ${meta.color}`,
      borderRadius: '0 8px 8px 0',
      display:      'flex',
      gap:          24,
      alignItems:   'flex-start',
      flexWrap:     'wrap',
    }}>
      {/* Identity */}
      <div style={{ minWidth: 140 }}>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Personagem</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 22 }}>{meta.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{char.name}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{meta.label}</div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
          Criado em {new Date(char.createdAt).toLocaleDateString('pt-BR')}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Atributos base</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto)', gap: '6px 20px' }}>
            {[
              { label: 'HP',  value: stats.hp },
              { label: 'AP',  value: stats.ap },
              { label: 'MP',  value: stats.mp },
              { label: 'FOR', value: stats.strength },
              { label: 'INT', value: stats.intelligence },
              { label: 'AGI', value: stats.agility },
              { label: 'INI', value: stats.initiative },
            ].map(({ label, value }) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function UsersPage() {
  const [users,   setUsers]   = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // Search & filter
  const [search,     setSearch]     = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'user' | 'admin'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'blocked'>('all');

  // Expanded character detail (user id)
  const [expanded, setExpanded] = useState<string | null>(null);

  // Per-row action loading
  const [actioning, setActioning] = useState<string | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    userApi.list()
      .then(setUsers)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !u.email.toLowerCase().includes(q)) return false;
      if (filterRole !== 'all' && u.role !== filterRole) return false;
      if (filterStatus === 'active'  && u.blocked)  return false;
      if (filterStatus === 'blocked' && !u.blocked) return false;
      return true;
    });
  }, [users, search, filterRole, filterStatus]);

  // ── Block / Unblock ───────────────────────────────────────────────────────
  const handleToggleBlock = async (user: AdminUser) => {
    setActioning(user.id);
    try {
      const fn = user.blocked ? userApi.unblock : userApi.block;
      await fn(user.id);
      setUsers((prev) =>
        prev.map((u) => u.id === user.id ? { ...u, blocked: !user.blocked, online: user.blocked ? u.online : false } : u)
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActioning(null);
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalUsers   = users.filter(u => u.role === 'user').length;
  const blockedCount = users.filter(u => u.blocked).length;
  const onlineCount  = users.filter(u => u.online).length;
  const charCount    = users.filter(u => u.character !== null).length;

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <div className="adminHeader"><h1>👥 Utilizadores</h1></div>
      <div className="adminContent"><div className="spinner" /></div>
    </>
  );

  return (
    <>
      <div className="adminHeader">
        <h1>👥 Utilizadores</h1>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        {/* ── Summary stats ── */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Utilizadores',  value: totalUsers,   color: 'var(--accent)' },
            { label: 'Online agora',  value: onlineCount,  color: '#44cc88' },
            { label: 'Com personagem',value: charCount,    color: '#88aaff' },
            { label: 'Bloqueados',    value: blockedCount, color: 'var(--danger)' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: 'var(--surface)',
              border:     '1px solid var(--border)',
              borderRadius: 8,
              padding:    '10px 18px',
              minWidth:   110,
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="adminCard" style={{ marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search */}
            <input
              type="text"
              className="formInput"
              placeholder="Pesquisar por e-mail…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200, maxWidth: 340 }}
            />

            {/* Role filter */}
            <select
              className="formInput"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as typeof filterRole)}
              style={{ width: 'auto', paddingRight: 28 }}
            >
              <option value="all">Todos os roles</option>
              <option value="user">Jogadores</option>
              <option value="admin">Admins</option>
            </select>

            {/* Status filter */}
            <select
              className="formInput"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
              style={{ width: 'auto', paddingRight: 28 }}
            >
              <option value="all">Todos os estados</option>
              <option value="active">Ativas</option>
              <option value="blocked">Bloqueadas</option>
            </select>

            <span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 'auto' }}>
              {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="adminCard">
          <div className="adminCardHeader" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Lista de Utilizadores</span>
          </div>

          {filtered.length === 0 ? (
            <div className="emptyState" style={{ padding: '32px 0' }}>
              {users.length === 0 ? 'Nenhum utilizador registado.' : 'Nenhum resultado para os filtros actuais.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['E-mail', 'Role', 'Estado', 'Sessão', 'Personagem', 'Registado', 'Ações'].map((h) => (
                      <th key={h} style={{
                        padding:    '9px 14px',
                        textAlign:  'left',
                        fontSize:   10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.07em',
                        color:      'var(--text-dim)',
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => (
                    <>
                      <tr
                        key={user.id}
                        style={{
                          borderBottom:   '1px solid var(--border)',
                          background:     user.blocked ? 'rgba(255,60,60,0.04)' : 'transparent',
                          transition:     'background 0.15s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = user.blocked ? 'rgba(255,60,60,0.07)' : 'rgba(255,255,255,0.03)')}
                        onMouseLeave={e => (e.currentTarget.style.background = user.blocked ? 'rgba(255,60,60,0.04)' : 'transparent')}
                      >
                        {/* Email */}
                        <td style={{ padding: '10px 14px', color: 'var(--text)', fontWeight: 500 }}>
                          {user.email}
                        </td>

                        {/* Role */}
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{
                            display:      'inline-block',
                            padding:      '2px 8px',
                            borderRadius: 12,
                            fontSize:     11,
                            fontWeight:   700,
                            background:   user.role === 'admin' ? 'rgba(200,160,60,0.15)' : 'rgba(100,160,255,0.1)',
                            color:        user.role === 'admin' ? '#c8a040' : '#88aaff',
                            textTransform:'uppercase',
                            letterSpacing:'0.05em',
                          }}>
                            {user.role === 'admin' ? 'Admin' : 'Jogador'}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{
                            display:      'inline-flex',
                            alignItems:   'center',
                            gap:          5,
                            padding:      '2px 10px',
                            borderRadius: 12,
                            fontSize:     11,
                            fontWeight:   700,
                            background:   user.blocked ? 'rgba(255,60,60,0.12)' : 'rgba(60,200,80,0.1)',
                            color:        user.blocked ? '#ff6060' : '#44cc66',
                            textTransform:'uppercase',
                            letterSpacing:'0.05em',
                          }}>
                            <span style={{
                              width:        6,
                              height:       6,
                              borderRadius: '50%',
                              background:   user.blocked ? '#ff4040' : '#44cc44',
                              flexShrink:   0,
                            }} />
                            {user.blocked ? 'Bloqueada' : 'Ativa'}
                          </span>
                        </td>

                        {/* Session */}
                        <td style={{ padding: '10px 14px' }}>
                          {user.online ? (
                            <span style={{ fontSize: 11, color: '#44ff88', fontWeight: 600 }}>● Online</span>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Offline</span>
                          )}
                        </td>

                        {/* Character */}
                        <td style={{ padding: '10px 14px' }}>
                          {user.character ? (
                            <button
                              style={{
                                background: 'none',
                                border:     'none',
                                cursor:     'pointer',
                                display:    'flex',
                                alignItems: 'center',
                                gap:        6,
                                padding:    0,
                                color:      CLASS_META[user.character.class]?.color ?? 'var(--text)',
                              }}
                              title="Ver detalhes do personagem"
                              onClick={() => setExpanded(expanded === user.id ? null : user.id)}
                            >
                              <span style={{ fontSize: 16 }}>{CLASS_META[user.character.class]?.icon ?? '⚔'}</span>
                              <span style={{ fontWeight: 600, fontSize: 12 }}>{user.character.name}</span>
                              <span style={{ fontSize: 11, opacity: 0.6 }}>{expanded === user.id ? '▲' : '▼'}</span>
                            </button>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>—</span>
                          )}
                        </td>

                        {/* Registered */}
                        <td style={{ padding: '10px 14px', color: 'var(--text-dim)', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          {user.role !== 'admin' && (
                            <button
                              className={`btn ${user.blocked ? 'btnSecondary' : 'btnDanger'}`}
                              style={{ fontSize: 11, padding: '4px 12px' }}
                              disabled={actioning === user.id}
                              onClick={() => void handleToggleBlock(user)}
                            >
                              {actioning === user.id
                                ? '…'
                                : user.blocked ? '🔓 Desbloquear' : '🔒 Bloquear'}
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* Expanded character details */}
                      {expanded === user.id && user.character && (
                        <tr key={`${user.id}-detail`} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td colSpan={7} style={{ padding: '0 14px 10px' }}>
                            <CharacterPanel char={user.character} />
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
